import React, {useState, useEffect} from 'react'
import {useTranslation} from 'react-i18next'
import axios from 'axios'
import {KTSVG} from '../../_metronic/helpers'
import {BACKEND_UPLOAD_URL, BACKEND_API_V2} from '../../config/config'

interface Product {
  id: number
  card_name: string
  image_url: string
  obtained: number
}

interface ProductsModalProps {
  isOpen: boolean
  onClose: () => void
  inspectorMode?: number
  inspectorId?: string
}


const ProductsModal: React.FC<ProductsModalProps> = ({isOpen, onClose, inspectorMode, inspectorId}) => {
  const {t} = useTranslation()
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(false)
  const [userCredits, setUserCredits] = useState(0)
  const [selectedProducts, setSelectedProducts] = useState<number[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      setError(null)
      fetchProducts()
    }
  }, [isOpen])

  const fetchProducts = async () => {
    setLoading(true)
    try {
      const tokenValue = localStorage.getItem('token')

      const apiUrl = `${BACKEND_API_V2}/user/mastery`

      const params: Record<string, string> = {}
      if (inspectorMode === 1 && inspectorId) {
        params.inspectorMode = '1'
        params.inspectorId = inspectorId
      }

      const response = await axios.get(apiUrl, {
        params,
        headers: {'x-access-token': `${tokenValue}`}
      })
      const {available_credits, mastery} = response.data

      setProducts(mastery || [])
      setUserCredits(available_credits || 0)
    } catch (error: any) {
      console.error('Error fetching mastery products:', error)

      if (error.code === 'ERR_NETWORK' || error.message?.includes('CORS')) {
        setError(t('MASTERY_ERROR_NETWORK'))
        setProducts([])
      } else {
        setError(t('MASTERY_ERROR_LOAD_FAILED'))
        setProducts([])
      }
    } finally {
      setLoading(false)
    }
  }


  const handleProductSelect = (productId: number) => {
    const product = products.find(p => p.id === productId)
    if (!product || product.obtained === 1) return

    setSelectedProducts(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId)
      } else {
        return [...prev, productId]
      }
    })
  }

  const handleSelectAll = () => {
    const availableProducts = products.filter(p => p.obtained === 0)
    setSelectedProducts(availableProducts.map(p => p.id))
  }

  const handleSubmit = async () => {
    if (selectedProducts.length === 0) return

    setSubmitting(true)

    try {
      const tokenValue = localStorage.getItem('token')

      let redeemUrl = `${BACKEND_API_V2}/user/mastery/redeem`
      if (inspectorMode === 1 && inspectorId) {
        redeemUrl += `?inspectorMode=1&inspectorId=${inspectorId}`
      }

      const response = await axios.post(redeemUrl, {
        course_ids: selectedProducts
      }, {
        headers: {'x-access-token': `${tokenValue}`}
      })

      const {status, message, redeemed_courses, credits_used, remaining_credits} = response.data

      if (status === 'success') {
        // Refresh products and credits after successful redemption
        await fetchProducts()
        setSelectedProducts([])

        // Show success message with details
        alert(`${t('MASTERY_SUCCESS_REDEEMED')}\n${message}\n${t('MASTERY_CREDITS_USED')}: ${credits_used}\n${t('MASTERY_REMAINING_CREDITS')}: ${remaining_credits}`)
      } else {
        alert(`${t('MASTERY_ERROR_REDEEM_FAILED')}: ${message}`)
      }
    } catch (error) {
      console.error('Error redeeming mastery products:', error)
      alert(t('MASTERY_ERROR_REDEEM_ERROR'))
    } finally {
      setSubmitting(false)
    }
  }

  const getTotalPrice = () => {
    return selectedProducts.length
  }

  const canSubmit = selectedProducts.length > 0 && (userCredits === -100 || getTotalPrice() <= userCredits) && !submitting

  if (!isOpen) return null

  return (
    <>
      <div 
        className='modal-backdrop show'
        style={{zIndex: 1050}}
        onClick={onClose}
      />
      <div 
        className='modal show d-block' 
        style={{zIndex: 1055}}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose()
          }
        }}
      >
        <div className='modal-dialog modal-dialog-centered modal-xl'>
        <div className='modal-content'>
          <div className='modal-header'>
            <h3 className='fw-bolder m-0'>{t('MASTERY_PRODUCTS_TITLE')}</h3>
            <button
              className='btn btn-sm btn-icon btn-active-color-primary'
              onClick={onClose}
            >
              <span className='svg-icon svg-icon-1'>
                <KTSVG path='/media/icons/duotune/general/gen040.svg' />
              </span>
            </button>
          </div>
          
          <div className='modal-body py-10 px-lg-17'>
            {error && (
              <div className='alert alert-danger mb-4'>
                <div className='d-flex align-items-center justify-content-between'>
                  <div className='d-flex align-items-center'>
                    <KTSVG path='/media/icons/duotune/general/gen044.svg' className='svg-icon-2x me-3' />
                    <div>{error}</div>
                  </div>
                  <button 
                    className='btn btn-sm btn-light-danger'
                    onClick={() => {
                      setError(null)
                      fetchProducts()
                    }}
                  >
                    {t('MASTERY_RETRY')}
                  </button>
                </div>
              </div>
            )}
            
            <div className='row g-3'>
              {/* Left side - User Credits */}
              <div className='col-12 col-md-4'>
                <div className='card h-100'>
                  <div className='card-body d-flex align-items-center justify-content-center'>
                    <div className='d-flex align-items-center'>
                      <div className='symbol symbol-50px me-4'>
                        <div className='symbol-label bg-light-primary'>
                          <KTSVG path='/media/icons/duotune/finance/fin006.svg' className='svg-icon-2x' />
                        </div>
                      </div>
                      <div className='fs-2 fw-bolder me-3'>{userCredits === -100 ? '∞' : Math.floor(userCredits)}</div>
                      <div className='text-muted'>{userCredits === -100 ? t('MASTERY_CREDITS_UNLIMITED') : t('MASTERY_CREDITS_AVAILABLE')}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right side - Products Grid */}
              <div className='col-12 col-md-8'>
                <div className='mb-4 text-center'>
                  <h5>{t('MASTERY_AVAILABLE_PRODUCTS')}</h5>
                </div>

                {loading ? (
                  <div className='text-center py-10'>
                    <div className='spinner-border text-primary' role='status'>
                      <span className='visually-hidden'>Loading...</span>
                    </div>
                    <div className='mt-3 text-muted'>{t('MASTERY_LOADING_PRODUCTS')}</div>
                  </div>
                ) : products.length === 0 ? (
                  <div className='text-center py-10'>
                    <KTSVG path='/media/icons/duotune/general/gen045.svg' className='svg-icon-3x text-muted mb-3' />
                    <div className='text-muted'>{t('MASTERY_NO_PRODUCTS')}</div>
                  </div>
                ) : (
                  <div className='row g-3'>
                    {products.map((product) => (
                      <div key={product.id} className='col-12 col-sm-6 col-md-6 col-lg-4'>
                        <div 
                          className={`d-flex border rounded mb-3 p-4 h-100 cursor-pointer ${
                            product.obtained === 1 
                              ? 'bg-light-success border-success' 
                              : selectedProducts.includes(product.id) 
                                ? 'bg-light-primary border-primary' 
                                : 'bg-light border-secondary'
                          }`}
                          style={{
                            cursor: product.obtained === 1 ? 'default' : 'pointer'
                          }}
                          onClick={() => handleProductSelect(product.id)}
                        >
                          <div className='d-flex flex-column justify-content-center align-items-center text-center w-100'>
                            <div 
                              className='symbol mx-auto mb-4'
                              style={{width: '160px', height: '90px', overflow: 'hidden'}}
                            >
                              <img 
                                src={BACKEND_UPLOAD_URL + product.image_url} 
                                className='symbol-label'
                                style={{objectFit: 'cover', width: '100%', height: '100%'}}
                                onError={(e) => {
                                  e.currentTarget.src = '/media/images/blank-image.svg'
                                }}
                              />
                            </div>
                            
                            <h6 className='card-title mb-3'>{product.card_name}</h6>
                            
                            <div className='mt-auto' style={{minHeight: '24px'}}>
                              {product.obtained === 1 ? (
                                <span className='badge badge-success'>{t('MASTERY_OBTAINED')}</span>
                              ) : selectedProducts.includes(product.id) && (
                                <span className='badge badge-primary'>{t('MASTERY_SELECTED')}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className='modal-footer'>
            <button
              type='button'
              className='btn btn-light'
              onClick={onClose}
            >
              {t('BTN_TEXT_CANCEL')}
            </button>
            <button
              type='button'
              className='btn btn-primary'
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {submitting ? (
                <>
                  <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                  {t('MASTERY_REDEEMING')}
                </>
              ) : (
                t('MASTERY_REDEEM_ITEMS', { count: selectedProducts.length })
              )}
            </button>
          </div>
        </div>
        </div>
      </div>
    </>
  )
}

export {ProductsModal}
